/**
 * Mixed overlay stacking (MatDialog / NgbModal / SweetAlert2).
 *
 * Each system uses its own default z-index. Opening one on top of another
 * without closing the first must raise the new layer above the current top.
 * Do not globally override `.modal` or `.cdk-overlay-container` (VDS-3114).
 */

const OVERLAY_LAYER_SELECTORS = [
  '.cdk-overlay-container',
  '.cdk-overlay-pane',
  'ngb-modal-window',
  '.modal.show',
  '.swal2-container',
].join(',');

const OVERLAY_Z_INDEX_STEP = 10;
const OVERLAY_Z_INDEX_FLOOR = 1050;

function isExcluded(el: Element, exclude: readonly Element[]): boolean {
  return exclude.some((item) => item === el || item.contains(el) || el.contains(item));
}

export function getTopOverlayZIndex(exclude: readonly Element[] = []): number {
  let maxZ = 0;
  document.querySelectorAll(OVERLAY_LAYER_SELECTORS).forEach((el) => {
    if (isExcluded(el, exclude)) {
      return;
    }
    const z = parseInt(window.getComputedStyle(el).zIndex, 10);
    if (!Number.isNaN(z) && z > maxZ) {
      maxZ = z;
    }
  });
  return maxZ;
}

export function nextOverlayZIndex(exclude: readonly Element[] = []): number {
  return Math.max(getTopOverlayZIndex(exclude), OVERLAY_Z_INDEX_FLOOR - OVERLAY_Z_INDEX_STEP) + OVERLAY_Z_INDEX_STEP;
}

export function applyOverlayZIndex(element: HTMLElement | null | undefined, zIndex: number): void {
  if (!element) {
    return;
  }
  element.style.setProperty('z-index', String(zIndex), 'important');
}

export function stackNgbModalAboveCurrentOverlays(
  modalWindow: HTMLElement | null | undefined,
  backdrop: HTMLElement | null | undefined,
): void {
  const exclude = [modalWindow, backdrop].filter((el): el is HTMLElement => !!el);
  const windowZ = nextOverlayZIndex(exclude);
  applyOverlayZIndex(modalWindow, windowZ);
  applyOverlayZIndex(backdrop, windowZ - 1);
}

export function stackSwalAboveCurrentOverlays(): void {
  const container = document.querySelector('.swal2-container') as HTMLElement | null;
  if (!container) {
    return;
  }
  applyOverlayZIndex(container, nextOverlayZIndex([container]));
}
